import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function KirstenhofPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Kirstenhof Painters Cape Town | Professional Painting Services | Residential Property Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Kirstenhof properties. Expert residential painting, home services, interior & exterior painting. Serving Kirstenhof Cape Town with premium quality finishes and community-focused service.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Kirstenhof painters, residential painting, home painting services, Kirstenhof property painting, professional painters Cape Town');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/kirstenhof`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Kirstenhof Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Kirstenhof properties with residential expertise and community-focused service.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/kirstenhof`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Kirstenhof",
        "description": "Specialized painting services for Kirstenhof's residential properties and homes.",
        "areaServed": "Kirstenhof, Cape Town",
        "serviceType": "Residential Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Kirstenhof')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Residential Home Painting',
      description: 'Complete painting services for Kirstenhof residential homes, from single-family properties to cluster developments with professional quality throughout.',
      price: 'From R10,000',
      features: ['All home types', 'Quality materials', 'Professional finish', 'Warranty included']
    },
    {
      icon: 'ri-building-line',
      title: 'Cluster & Complex Painting',
      description: 'Specialized painting for Kirstenhof residential complexes and cluster homes with coordinated service and community-focused approach.',
      price: 'From R12,000',
      features: ['Multi-unit coordination', 'Minimal disruption', 'Consistent quality', 'Efficient scheduling']
    },
    {
      icon: 'ri-brush-line',
      title: 'Interior Painting',
      description: 'Expert interior painting services for Kirstenhof homes including all living spaces with attention to detail and quality finishes.',
      price: 'From R7,000',
      features: ['Room preparation', 'Premium paints', 'Color consultation', 'Clean execution']
    },
    {
      icon: 'ri-palette-line',
      title: 'Exterior Painting',
      description: 'Professional exterior painting protecting Kirstenhof homes from weather while enhancing curb appeal and maintaining property value.',
      price: 'From R9,000',
      features: ['Weather protection', 'Surface preparation', 'Durable coatings', 'Color matching']
    },
    {
      icon: 'ri-tools-line',
      title: 'Home Improvement Painting',
      description: 'Specialized painting for Kirstenhof home improvements and upgrades, coordinating with renovations for seamless results.',
      price: 'From R8,000',
      features: ['Renovation support', 'Trade coordination', 'Timeline management', 'Quality assurance']
    },
    {
      icon: 'ri-shield-line',
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting services to protect your Kirstenhof property investment with scheduled inspections and preventive care.',
      price: 'From R4,500',
      features: ['Scheduled visits', 'Preventive maintenance', 'Priority service', 'Long-term savings']
    }
  ];

  const testimonials = [
    {
      name: 'Patricia Wilson',
      property: 'Cluster Home',
      rating: 5,
      text: 'Excellent service for our Kirstenhof cluster home. They coordinated well with our complex management and the results are beautiful. Very professional team.',
      image: 'https://public.readdy.ai/ai/img_res/9b47b4f755c5bc30dc21db10a3531676.jpg'
    },
    {
      name: 'James Mitchell',
      property: 'Family Home',
      rating: 5,
      text: 'Outstanding work on our Kirstenhof home. The team was punctual, professional, and the quality exceeded our expectations. Highly recommend their services.',
      image: 'https://readdy.ai/api/search-image?query=Happy%20man%20homeowner%20in%20Kirstenhof%20family%20home%20with%20painted%20interior%2C%20satisfied%20client%20portrait%2C%20modern%20residential%20property&width=80&height=80&seq=kirstenhof2&orientation=squarish'
    },
    {
      name: 'Susan Taylor',
      property: 'Residential Property',
      rating: 5,
      text: 'Professional team with great attention to detail. They transformed our Kirstenhof property beautifully and were very respectful of our home throughout the project.',
      image: 'https://readdy.ai/api/search-image?query=Satisfied%20woman%20homeowner%20in%20Kirstenhof%20residential%20property%20with%20newly%20painted%20rooms%2C%20happy%20customer%20portrait&width=80&height=80&seq=kirstenhof5&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Cluster Complex Renovation',
      description: 'Complete exterior painting for Kirstenhof cluster complex with coordinated colors and efficient multi-unit scheduling',
      image: 'https://readdy.ai/api/search-image?query=Kirstenhof%20cluster%20complex%20with%20freshly%20painted%20exteriors%2C%20coordinated%20colors%2C%20professional%20painting%20work%2C%20residential%20development&width=400&height=300&seq=kirstenhof3&orientation=landscape'
    },
    {
      title: 'Family Home Transformation',
      description: 'Interior and exterior painting for Kirstenhof family home with modern color palette and durable finishes',
      image: 'https://readdy.ai/api/search-image?query=Kirstenhof%20family%20home%20with%20newly%20painted%20exterior%2C%20modern%20colors%2C%20professional%20finish%2C%20residential%20property&width=400&height=300&seq=kirstenhof4&orientation=landscape'
    },
    {
      title: 'Residential Complex Update',
      description: 'Multi-property painting project for Kirstenhof residential complex with premium materials and coordinated service',
      image: 'https://readdy.ai/api/search-image?query=Kirstenhof%20residential%20complex%20with%20updated%20painted%20exteriors%2C%20professional%20maintenance%20work%2C%20community%20development&width=400&height=300&seq=kirstenhof6&orientation=landscape'
    },
    {
      title: 'Home Improvement Project',
      description: 'Complete painting service for Kirstenhof home improvement including interior updates and exterior refresh',
      image: 'https://readdy.ai/api/search-image?query=Kirstenhof%20home%20with%20improvement%20painting%2C%20modern%20updates%2C%20professional%20painting%20finish%2C%20residential%20property&width=400&height=300&seq=kirstenhof7&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Property Assessment',
      description: 'Comprehensive evaluation of your Kirstenhof property including architectural style, current condition, and specific requirements.'
    },
    {
      step: '2',
      title: 'Community Coordination',
      description: 'For cluster and complex properties, coordination with management and neighbors to ensure smooth execution and minimal disruption.'
    },
    {
      step: '3',
      title: 'Thorough Preparation',
      description: 'Meticulous surface preparation including cleaning, repairs, and priming to ensure optimal paint adhesion and lasting results.'
    },
    {
      step: '4',
      title: 'Professional Application',
      description: 'Expert painting using quality materials and proven techniques for superior finish quality and long-term durability.'
    },
    {
      step: '5',
      title: 'Quality Inspection',
      description: 'Detailed inspection and final touch-ups ensuring every aspect meets our high standards and your expectations.'
    },
    {
      step: '6',
      title: 'Maintenance Guidance',
      description: 'Comprehensive care instructions and maintenance recommendations specific to your Kirstenhof property\'s needs.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20residential%20home%20in%20Kirstenhof%20Cape%20Town%2C%20painting%20equipment%2C%20scaffolding%2C%20beautiful%20suburban%20property%2C%20sunny%20day&width=1920&height=1080&seq=kirstenhofhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Kirstenhof
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Kirstenhof residential properties and cluster homes. Specialized community-focused painting with coordinated service and quality finishes for your property with professional results and lasting value.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Kirstenhof Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Community-Focused Painting Services in Kirstenhof
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Kirstenhof is a well-established residential suburb in Cape Town's Southern Suburbs, known for its strong community spirit, excellent schools, and convenient location. This popular area attracts families and professionals who appreciate the combination of suburban tranquility, quality amenities, and easy access to major transport routes including the M3 motorway. The suburb features a diverse mix of property types including single-family homes, cluster developments, and residential complexes, creating a vibrant and welcoming community atmosphere.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The residential character of Kirstenhof presents unique opportunities for painting services that understand community dynamics and property management requirements. Many properties in the area are part of cluster developments or residential complexes, requiring painting solutions that coordinate with body corporate regulations and community standards. The suburb's established nature means properties benefit from regular maintenance and updates, while newer developments require quality finishes that meet modern expectations and enhance property values.
              </p>
              <p className="text-lg text-gray-600">
                Our team specializes in working with Kirstenhof properties, understanding the importance of community coordination, professional communication, and quality results. We have extensive experience working with body corporates, homeowner associations, and individual property owners to deliver painting services that meet community standards while respecting residents' needs. Whether you need painting for a single home, cluster unit, or entire residential complex, we provide professional service that enhances your property's value and contributes positively to the community's appearance.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Kirstenhof%20Cape%20Town%20residential%20area%20with%20cluster%20homes%2C%20suburban%20setting%2C%20community%20development%2C%20established%20neighborhood&width=600&height=400&seq=kirstenhof8&orientation=landscape"
                alt="Kirstenhof Residential Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Kirstenhof Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Kirstenhof's residential properties and community developments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Kirstenhof Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A community-focused approach ensuring exceptional results with proper coordination and professional execution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Kirstenhof */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Kirstenhof?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in residential and cluster property painting with proven results and satisfied Kirstenhof residents.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-community-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Community Experience</h3>
              <p className="text-gray-600">Extensive experience working with Kirstenhof residential complexes, cluster developments, and body corporates with professional coordination.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-team-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Professional Coordination</h3>
              <p className="text-gray-600">Skilled at coordinating with property management, body corporates, and multiple stakeholders for smooth project execution.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Quality Assurance</h3>
              <p className="text-gray-600">Comprehensive quality control and warranties ensuring your Kirstenhof property receives the best possible results and protection.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-customer-service-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Resident Respect</h3>
              <p className="text-gray-600">Professional team that respects residents' needs, maintains clean work areas, and minimizes disruption to community life.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Kirstenhof Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Kirstenhof residential and cluster property painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kirstenhof Resident Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Kirstenhof property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cluster & Complex Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cluster & Complex Painting Expertise
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Kirstenhof Communities Choose Us</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-file-list-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Body Corporate Coordination</h4>
                    <p className="text-gray-600">Experienced in working with body corporates and homeowner associations, understanding compliance requirements and community standards for seamless project execution.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-calendar-check-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Efficient Multi-Unit Scheduling</h4>
                    <p className="text-gray-600">Specialized scheduling systems for cluster and complex projects, coordinating multiple units efficiently while minimizing disruption to residents and maintaining quality standards.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-palette-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Consistent Color Coordination</h4>
                    <p className="text-gray-600">Expert color matching and coordination across multiple units ensuring consistent appearance and maintaining community aesthetic standards throughout the complex.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Volume Pricing Benefits</h4>
                    <p className="text-gray-600">Competitive pricing for multi-unit projects providing cost savings for body corporates and homeowner associations while maintaining premium quality standards.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Kirstenhof%20cluster%20complex%20with%20coordinated%20painted%20exteriors%2C%20residential%20development%2C%20community%20living%2C%20professional%20maintenance&width=600&height=500&seq=kirstenhof9&orientation=portrait"
                alt="Kirstenhof Cluster Complex"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Kirstenhof Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's residential and cluster property painting specialists for Kirstenhof. Our expert team understands the unique requirements of community developments and is ready to provide you with a comprehensive consultation and detailed quote. Experience the difference that community-focused painting expertise makes for your Kirstenhof property with professional coordination and quality results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
